import { Box, Typography, LinearProgress, useTheme } from "@mui/material";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useMemo, memo, useCallback } from "react";

/**
 * Premium Facility Utilization Widget
 * Displays asset distribution across facilities with horizontal bar chart
 * Memoized to prevent unnecessary re-renders
 */
const FacilityUtilization = memo(function FacilityUtilization(props) {
  const { assets = [], facilities = {}, onFacilityClick = () => {} } = props;
  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, mode } = palette;
  const isDark = mode === "dark";

  // Brand colors
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";

  // Chart colors for different facilities
  const chartColors = [
    greenColor,
    "#3b82f6",
    "#8b5cf6",
    "#06b6d4",
    "#f59e0b",
    "#ec4899",
    "#10b981",
    "#6366f1",
    "#14b8a6",
    "#f97316",
  ];

  // Theme-aware colors
  const textPrimary = isDark ? "#ffffff" : "#1e293b";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";
  const borderColor = isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0, 0, 0, 0.08)";
  const barBgColor = isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0, 0, 0, 0.06)";

  // Calculate facility utilization
  const facilityData = useMemo(() => {
    const facilityCounts = {};
    let totalAssets = 0;
    let unassignedCount = 0;

    assets?.forEach(asset => {
      totalAssets++;
      const facilityId = asset.facilityId;
      
      if (facilityId && facilities[facilityId]) {
        if (!facilityCounts[facilityId]) {
          facilityCounts[facilityId] = {
            id: facilityId,
            name: facilities[facilityId].name || "Unknown Facility",
            count: 0,
          };
        }
        facilityCounts[facilityId].count++;
      } else {
        unassignedCount++;
      }
    });

    // Convert to array and sort by count
    const sortedFacilities = Object.values(facilityCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 8); // Top 8 facilities

    // Add unassigned if there are any
    if (unassignedCount > 0) {
      sortedFacilities.push({
        id: "unassigned",
        name: "Unassigned / Unknown",
        count: unassignedCount,
        isUnassigned: true,
      });
    }

    // Calculate max for percentage
    const maxCount = sortedFacilities.length > 0
      ? Math.max(...sortedFacilities.map(f => f.count))
      : 1;

    return {
      facilities: sortedFacilities.map((f, index) => ({
        ...f,
        percentage: (f.count / maxCount) * 100,
        globalPercentage: totalAssets > 0 ? ((f.count / totalAssets) * 100).toFixed(1) : 0,
        color: f.isUnassigned ? textSecondary : chartColors[index % chartColors.length],
      })),
      totalAssets,
      totalFacilities: Object.keys(facilityCounts).length,
    };
  }, [assets, facilities, chartColors, textSecondary]);

  // Facility Bar Component with accessibility
  const FacilityBar = ({ facility, index }) => {
    const handleClick = useCallback(() => {
      if (!facility.isUnassigned) {
        onFacilityClick(facility.id);
      }
    }, [facility.id, facility.isUnassigned]);

    const handleKeyDown = useCallback((e) => {
      if ((e.key === 'Enter' || e.key === ' ') && !facility.isUnassigned) {
        e.preventDefault();
        onFacilityClick(facility.id);
      }
    }, [facility.id, facility.isUnassigned]);

    return (
    <Box
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={facility.isUnassigned ? -1 : 0}
      aria-label={`${facility.name}: ${facility.count} assets (${facility.globalPercentage}%)`}
      aria-disabled={facility.isUnassigned}
      sx={{
        padding: "12px 14px",
        borderRadius: "12px",
        backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.01)",
        border: `1px solid ${borderColor}`,
        cursor: facility.isUnassigned ? "default" : "pointer",
        transition: "all 0.2s ease",
        animation: `barSlide 0.4s ease-out ${index * 0.05}s both`,
        "@keyframes barSlide": {
          from: { opacity: 0, transform: "translateX(-20px)" },
          to: { opacity: 1, transform: "translateX(0)" },
        },
        "&:hover, &:focus": !facility.isUnassigned ? {
          backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
          borderColor: facility.color,
          transform: "translateX(4px)",
          outline: "none",
        } : {},
        "&:focus-visible": {
          outline: `2px solid ${facility.color}`,
          outlineOffset: "2px",
        },
      }}
    >
      {/* Header Row */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              backgroundColor: `${facility.color}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <WarehouseIcon sx={{ fontSize: "1rem", color: facility.color }} />
          </Box>
          <Typography
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: textPrimary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {facility.name}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px", flexShrink: 0 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: facility.color }}>
            {facility.count.toLocaleString()}
          </Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: textSecondary }}>
            ({facility.globalPercentage}%)
          </Typography>
        </Box>
      </Box>

      {/* Progress Bar */}
      <Box sx={{ position: "relative" }}>
        <LinearProgress
          variant="determinate"
          value={facility.percentage}
          aria-label={`${facility.name} utilization`}
          aria-valuenow={Math.round(facility.percentage)}
          aria-valuemin={0}
          aria-valuemax={100}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: barBgColor,
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              background: `linear-gradient(90deg, ${facility.color}, ${facility.color}cc)`,
              transition: "transform 0.6s ease-out",
            },
          }}
        />
      </Box>
    </Box>
    );
  };

  return (
    <Box
      component="article"
      role="region"
      aria-label="Facility Utilization"
      sx={{
        background: isDark
          ? "linear-gradient(145deg, #032d42 0%, #021e2d 100%)"
          : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
        borderRadius: "16px",
        border: `1px solid ${borderColor}`,
        boxShadow: isDark
          ? "0 4px 20px rgba(0, 0, 0, 0.3)"
          : "0 4px 20px rgba(0, 0, 0, 0.06)",
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: isDark
            ? `linear-gradient(90deg, ${greenColor} 0%, ${navyColor} 100%)`
            : `linear-gradient(90deg, ${navyColor} 0%, ${greenColor} 100%)`,
          borderRadius: "16px 16px 0 0",
        },
      }}
    >
      {/* Header – same pattern as map (icon 36 + title + subtitle) */}
      <Box
        component="header"
        sx={{
          padding: "12px 16px",
          background: "transparent",
          borderBottom: `1px solid ${borderColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: isDark
                ? `linear-gradient(135deg, ${greenColor}25 0%, ${greenColor}10 100%)`
                : `linear-gradient(135deg, ${greenColor}20 0%, ${greenColor}05 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TrendingUpIcon sx={{ color: greenColor, fontSize: "1.2rem" }} />
          </Box>
          <Box>
            <Typography
              component="h2"
              sx={{
                fontSize: "1rem",
                fontWeight: 600,
                color: isDark ? greenColor : navyColor,
                lineHeight: 1.2,
              }}
            >
              Facility Utilization
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: textSecondary }}>
              Asset distribution by location
            </Typography>
          </Box>
        </Box>

        {/* Summary Stats */}
        <Box sx={{ display: "flex", gap: "16px" }} role="group" aria-label="Summary statistics">
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: "0.6875rem", color: textSecondary }}>
              Total
            </Typography>
            <Typography 
              sx={{ fontSize: "1rem", fontWeight: 700, color: greenColor }}
              aria-label={`Total ${facilityData.totalAssets} assets`}
            >
              {facilityData.totalAssets.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: "0.6875rem", color: textSecondary }}>
              Facilities
            </Typography>
            <Typography 
              sx={{ fontSize: "1rem", fontWeight: 700, color: navyColor }}
              aria-label={`${facilityData.totalFacilities} facilities`}
            >
              {facilityData.totalFacilities}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Facility List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: "12px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: isDark ? "rgba(148, 163, 184, 0.3)" : "rgba(0, 0, 0, 0.2)",
            borderRadius: "3px",
          },
        }}
      >
        {facilityData.facilities.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              padding: "40px 20px",
            }}
          >
            <Box
              sx={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                background: `${navyColor}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "16px",
              }}
            >
              <LocationOnIcon sx={{ fontSize: "1.75rem", color: navyColor }} />
            </Box>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: textPrimary, marginBottom: "4px" }}>
              No Facility Data
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: textSecondary, textAlign: "center" }}>
              Asset locations will appear here
            </Typography>
          </Box>
        ) : (
          facilityData.facilities.map((facility, index) => (
            <FacilityBar key={facility.id} facility={facility} index={index} />
          ))
        )}
      </Box>

      {/* Footer */}
      {facilityData.facilities.length > 0 && (
        <Box
          component="footer"
          sx={{
            padding: "10px 16px",
            borderTop: `1px solid ${borderColor}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: "0.6875rem", color: textSecondary }}>
            Click on a facility to filter the map
          </Typography>
        </Box>
      )}
    </Box>
  );
});

export default FacilityUtilization;
